import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { appConfig } from "@/config/app";

/** Merge Tailwind classes with clsx */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Validate file type against accepted extensions */
export function isValidFileType(file: File): boolean {
  const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
  return (appConfig.upload.acceptedTypes as readonly string[]).includes(ext);
}

/** Validate file MIME type to prevent injection */
export function isValidMimeType(file: File): boolean {
  const validMimes = appConfig.upload.acceptedMimeTypes as readonly string[];
  // Some browsers don't set MIME for .xls; also check extension
  if (file.type === "" || file.type === "application/octet-stream") {
    return isValidFileType(file);
  }
  return validMimes.includes(file.type);
}

/** Validate file size */
export function isValidFileSize(file: File): boolean {
  return file.size <= appConfig.upload.maxFileSizeMB * 1024 * 1024;
}

/** Full file validation */
export function validateFile(file: File): { valid: boolean; error?: string } {
  if (!isValidFileType(file)) {
    return {
      valid: false,
      error: `Invalid file type. Accepted: ${appConfig.upload.acceptedTypes.join(", ")}`,
    };
  }
  if (!isValidMimeType(file)) {
    return { valid: false, error: `Could not upload ${file.name} – Invalid file type.` };
  }
  if (!isValidFileSize(file)) {
    return { valid: false, error: `File exceeds ${appConfig.upload.maxFileSizeMB}MB limit.` };
  }
  return { valid: true };
}

/** Format bytes to human-readable */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

/** Format ingestion/processing duration for display (e.g. "12.3s", "1m 5s"). */
export function formatIngestionDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
    const decimals = totalSeconds < 10 ? 1 : 0;
    return `${totalSeconds.toFixed(decimals)}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/** Format date for display */
export function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}
